import { useCallback, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { Paper, Stack, Typography } from '@rossum/ui/material';
import { User } from '../../../types/user';
import { FeatureToggles } from '../../feature-enablement-ui/FeatureToggles';
import {
  doesFeatureExistInLocalOverrides,
  isFeatureEnabled,
  isFeatureGloballyEnabled,
  setFeatureLocalOverride,
} from '../../feature-enablement/featureEnablement';
import {
  allFeatures,
  allFeaturesList,
  FeatureId,
} from '../../feature-enablement/features';

export const ChangeFeatureFlagsPage = ({ user }: { user: User }) => {
  // Trick to rerender after changing local storage without having to deal with subscriptions and stuff
  // Open to better suggestions (we could put this into zustand or something)
  const [rerenderState, setRerenderState] = useState(0);

  const userLabConfig = useMemo(
    () => user.uiSettings?.labConfig || {},
    [user.uiSettings?.labConfig]
  );

  const localFeaturesConfig = useMemo(() => {
    return R.mapValues(allFeatures, feature => ({
      enabled: isFeatureEnabled(feature.id, userLabConfig),
      displayAddControl: !doesFeatureExistInLocalOverrides(feature.id),
      externalFlagValue: isFeatureGloballyEnabled(feature.id),
    }));
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [userLabConfig, rerenderState]);

  const handleLocalFeatureOverrideChanged = useCallback(
    (featureId: FeatureId, value: boolean | null) => {
      setFeatureLocalOverride(featureId, value);
      setRerenderState(prev => prev + 1);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [setRerenderState, rerenderState]
  );

  const intl = useIntl();

  return (
    <Paper>
      <Stack>
        <Stack sx={{ p: 4 }}>
          <Typography variant="h5">
            {intl.formatMessage({
              id: 'features.personalSettings.changeFeatureFlags',
            })}
          </Typography>
        </Stack>

        <FeatureToggles
          features={allFeaturesList}
          config={localFeaturesConfig}
          onChange={handleLocalFeatureOverrideChanged}
          clearable
          showOverridesWarnings={false}
        />
      </Stack>
    </Paper>
  );
};
